import React, { useEffect, useMemo, useRef, useState } from 'react';
import './IntakeWizard.css';
import { financialService } from '../../services/financialService';
import { storageService } from '../../services/storageService';
import { activationService } from '../../services/activationService';
import InlineAlert from '../common/InlineAlert';
import { getUserFacingError } from '../../lib/userFacingErrors';

export default function IntakeWizard({ onCompleted }) {
  const KEY = 'intake_draft_v2';
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const stepTitleRef = useRef(null);
  const [form, setForm] = useState(() =>
    storageService.load(KEY, {
      current_objective: 'Save more',
      filing_status: 'single',
      zip: '',
      birth_year: 1990,
      risk_tolerance: 'moderate',
      housing_tenure: 'rent',
      gross_income_exact: 0,
      pay_frequency: 'biweekly',
      employer_match_pct: 0,
      employee_contribution_pct: 0,
    })
  );

  useEffect(() => {
    storageService.save(KEY, form);
  }, [form]);

  // Move focus to step heading on step change for keyboard/screen reader orientation
  useEffect(() => {
    stepTitleRef.current?.focus();
  }, [step]);

  useEffect(() => {
    (async () => {
      try {
        const res = await financialService.getProfile();
        if (res?.ok && res?.data) {
          setForm((f) => ({ ...f, ...res.data }));
        }
      } catch {}
    })();
  }, []);

  const steps = useMemo(() => ['Basic Information', 'Income & Retirement'], []);

  const validateStep = (stepIndex) => {
    const errors = {};
    if (stepIndex === 0) {
      if (!form.current_objective?.trim()) errors.current_objective = 'Current objective is required';
      if (!form.filing_status?.trim()) errors.filing_status = 'Filing status is required';
      if (!form.risk_tolerance?.trim()) errors.risk_tolerance = 'Risk tolerance is required';
      if (!form.housing_tenure?.trim()) errors.housing_tenure = 'Housing type is required';
      if (!form.zip || form.zip.length !== 5) errors.zip = 'ZIP code must be exactly 5 digits';
      if (form.birth_year == null) errors.birth_year = 'Birth year is required';
      else if (form.birth_year < 1900 || form.birth_year > 2026) errors.birth_year = 'Birth year must be between 1900 and 2026';
    } else if (stepIndex === 1) {
      if (!form.pay_frequency?.trim()) errors.pay_frequency = 'Pay frequency is required';
      if (form.gross_income_exact == null) errors.gross_income_exact = 'Income is required';
      else if (form.gross_income_exact < 0) errors.gross_income_exact = 'Income cannot be negative';
      if (form.employer_match_pct == null) errors.employer_match_pct = 'Employer match % is required';
      else if (form.employer_match_pct < 0 || form.employer_match_pct > 100) errors.employer_match_pct = 'Match % must be between 0 and 100';
      if (form.employee_contribution_pct == null) errors.employee_contribution_pct = 'Contribution % is required';
      else if (form.employee_contribution_pct < 0 || form.employee_contribution_pct > 100) errors.employee_contribution_pct = 'Contribution % must be between 0 and 100';
    }
    return errors;
  };

  const validateForm = () => ({ ...validateStep(0), ...validateStep(1) });

  const next = () => {
    const errors = validateStep(step);
    setValidationErrors(errors);
    if (Object.keys(errors).length === 0) setStep((s) => Math.min(s + 1, steps.length - 1));
  };

  const prev = () => {
    setValidationErrors({});
    setStep((s) => Math.max(s - 1, 0));
  };

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (validationErrors[k]) {
      setValidationErrors((e) => { const n = { ...e }; delete n[k]; return n; });
    }
  };

  const submit = async () => {
    const errors = validateForm();
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError({
        title: 'Check highlighted fields',
        message: 'Please fix validation errors before submitting.',
        severity: 'warning',
      });
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const sanitizedData = {
        current_objective: form.current_objective,
        filing_status: form.filing_status,
        zip: form.zip,
        birth_year: form.birth_year,
        gross_income_exact: form.gross_income_exact,
        pay_frequency: form.pay_frequency,
        risk_tolerance: form.risk_tolerance,
        housing_tenure: form.housing_tenure,
        employer_match_pct: form.employer_match_pct,
        employee_contribution_pct: form.employee_contribution_pct,
      };
      const res = await financialService.upsertProfile(sanitizedData);
      if (!res?.ok) throw new Error(res?.error || 'Failed to save');
      activationService.trackEvent('profile_completed', {
        source: 'activation',
        step: 'profile',
      });
      storageService.remove(KEY);
      onCompleted?.(res.data);
    } catch (e) {
      setError(getUserFacingError(e, 'profile-save'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="intake-page">
      {/* Top nav — logo only, no close/skip */}
      <nav className="intake-top-nav" aria-label="Setup navigation">
        <div className="intake-nav-brand">
          <span className="intake-logo-mark" aria-hidden="true">
            <img src={`${process.env.PUBLIC_URL}/wealth-y_logo.png`} alt="" />
          </span>
          <span className="intake-wordmark">Wealth-Y</span>
        </div>
      </nav>

      {/* Progress bar */}
      <div className="intake-progress-wrap" role="progressbar" aria-valuenow={step + 1} aria-valuemin={1} aria-valuemax={steps.length} aria-valuetext={`Step ${step + 1} of ${steps.length}: ${steps[step]}`}>
        <div className="intake-progress-header">
          <span className="intake-progress-eyebrow">PROFILE SETUP</span>
          <span className="intake-progress-step-label">Step {step + 1} of {steps.length}</span>
        </div>
        <div className="intake-progress-bar" aria-hidden="true">
          {steps.map((_, i) => (
            <div key={i} className={`intake-progress-segment${i < step ? ' completed' : i === step ? ' active' : ''}`} />
          ))}
        </div>
      </div>

      {/* Body */}
      <main className="intake-main">
        <h1 ref={stepTitleRef} tabIndex={-1} className="intake-step-title">{steps[step]}</h1>

        {step === 0 && (
          <div className="grid">
            <Field label="Current Objective" required error={validationErrors.current_objective}>
              <select value={form.current_objective} onChange={(e) => update('current_objective', e.target.value)}>
                <option value="Save more">Save More</option>
                <option value="Pay off debt">Pay Off Debt</option>
                <option value="Invest">Invest</option>
                <option value="Increase income">Increase Income</option>
              </select>
            </Field>
            <Field label="Tax Filing Status" required error={validationErrors.filing_status}>
              <select value={form.filing_status} onChange={(e) => update('filing_status', e.target.value)}>
                <option value="single">Single</option>
                <option value="married_joint">Married Filing Jointly</option>
                <option value="married_separate">Married Filing Separately</option>
                <option value="head_of_household">Head of Household</option>
              </select>
            </Field>
            <Field label="ZIP Code" required error={validationErrors.zip}>
              <input inputMode="numeric" maxLength={5} value={form.zip} onChange={(e) => update('zip', e.target.value.slice(0, 5))} />
            </Field>
            <Field label="Birth Year" required error={validationErrors.birth_year}>
              <input type="number" inputMode="numeric" min="1900" max="2100" value={form.birth_year ?? ''} onChange={(e) => update('birth_year', e.target.value === '' ? null : Number(e.target.value))} />
            </Field>
            <Field label="Investment Risk Tolerance" required error={validationErrors.risk_tolerance}>
              <select value={form.risk_tolerance} onChange={(e) => update('risk_tolerance', e.target.value)}>
                <option value="moderate">Moderate</option>
                <option value="conservative">Conservative</option>
                <option value="aggressive">Aggressive</option>
              </select>
            </Field>
            <Field label="Housing Status" required error={validationErrors.housing_tenure}>
              <select value={form.housing_tenure} onChange={(e) => update('housing_tenure', e.target.value)}>
                <option value="rent">Rent</option>
                <option value="own">Own</option>
              </select>
            </Field>
          </div>
        )}

        {step === 1 && (
          <div className="grid">
            <Field label="Gross Annual Income" required error={validationErrors.gross_income_exact}>
              <input
                type="text"
                inputMode="numeric"
                value={form.gross_income_exact == null ? '' : form.gross_income_exact.toLocaleString('en-US')}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '');
                  update('gross_income_exact', digits === '' ? null : Number(digits));
                }}
                placeholder="$0"
              />
            </Field>
            <Field label="Pay Frequency" required error={validationErrors.pay_frequency}>
              <select value={form.pay_frequency} onChange={(e) => update('pay_frequency', e.target.value)}>
                <option value="weekly">Weekly</option>
                <option value="biweekly">Bi-weekly</option>
                <option value="semimonthly">Semi-monthly</option>
                <option value="monthly">Monthly</option>
              </select>
            </Field>
            <Field label="Employer 401(k) Match (%)" required error={validationErrors.employer_match_pct} tooltip="Enter the percentage your employer matches. For example, enter 4 for 4% match">
              <input inputMode="decimal" type="number" min="0" max="100" step="0.01" value={form.employer_match_pct ?? ''} onChange={(e) => update('employer_match_pct', e.target.value === '' ? null : Number(e.target.value))} placeholder="e.g., 4" />
            </Field>
            <Field label="Your 401(k) Contribution (%)" required error={validationErrors.employee_contribution_pct} tooltip="Enter the percentage you contribute. For example, enter 6 for 6% contribution">
              <input inputMode="decimal" type="number" min="0" max="100" step="0.01" value={form.employee_contribution_pct ?? ''} onChange={(e) => update('employee_contribution_pct', e.target.value === '' ? null : Number(e.target.value))} placeholder="e.g., 6" />
            </Field>
          </div>
        )}

        {error && <InlineAlert title={error.title} message={error.message} tone={error.severity || 'error'} />}
      </main>

      {/* Footer nav */}
      <footer className="intake-footer">
        <div className="intake-footer-inner">
          {step > 0 ? (
            <button type="button" className="btn btn-secondary" onClick={prev} disabled={saving}>
              Previous
            </button>
          ) : (
            <span />
          )}
          {step < steps.length - 1 ? (
            <button type="button" className="btn btn-primary" onClick={next} disabled={saving}>
              Next
            </button>
          ) : (
            <button type="button" className="btn btn-primary" onClick={submit} disabled={saving}>
              {saving ? 'Saving…' : 'Complete'}
            </button>
          )}
        </div>
      </footer>
    </div>
  );
}

function Field({ label, required = false, tooltip, error, children }) {
  const [showTooltip, setShowTooltip] = React.useState(false);
  return (
    <label className={`field ${error ? 'field-error' : ''}`}>
      <div className="field-label">
        <span>{label}{required ? ' *' : ''}</span>
        {tooltip && (
          <span
            className="tooltip"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            onClick={() => setShowTooltip(!showTooltip)}
          >
            ?
            {showTooltip && <span className="tooltip-content">{tooltip}</span>}
          </span>
        )}
      </div>
      <div className="field-input">{children}</div>
      {error && <div className="field-error-msg">{error}</div>}
    </label>
  );
}
